/* eslint-disable react/prop-types */
import React, { useReducer, MutableRefObject } from 'react';
import { NotificationContainer } from '../../index';
import NotificationItem from './NotificationItem';
import {
  Action,
  ActionTypes,
  Context,
  EnqueueProps,
  Notification,
  NotificationType,
  ProviderProps,
  State,
} from './NotificationProps';

// TODO:
// - [x] Exclude types in a separate file.
// - [ ] Write tests.
// - [x] Write documentation.

const initialNotificationContextValue = {
  enqueue: () => '',
  close: () => {
    // noop
  },
};

export const NotificationContext = React.createContext<Context>(
  initialNotificationContextValue,
);

/**
 * Close the oldest non persistant notification.
 * This way only ONE notification is removed at a time.
 */
const handleCloseOldest = (state: State): State => {
  if (state.notifications.some(notification => notification.requestClose)) {
    return state;
  }

  let popped = false;

  // Reverse the array because we need the oldest entry first ...
  // ... reverse it back at the end to it's previous order.
  const newNotifications = state.notifications
    .reverse()
    .map(notification => {
      if (!popped && !notification.persist) {
        popped = true;

        return {
          ...notification,
          requestClose: true,
        };
      }

      return notification;
    })
    .reverse();

  return {
    ...state,
    notifications: newNotifications,
  };
};

/**
 * Removes the oldest enqueued notification from the `queue` and moves it into `notifications` state.
 */
const processQueue = (state: State): State => {
  const { queue, notifications } = state;

  if (queue.length > 0) {
    const item = state.queue[0];

    return {
      ...state,
      notifications: [item, ...notifications],
      queue: queue.slice(1, queue.length),
    };
  }

  return state;
};

/**
 * Returns the current state if there is no space left inside `notifications` based on `maxItems`.
 * Else it passes it onto `processQueue`.
 */
const handleDisplayItem = (state: State): State => {
  const { notifications, maxItems } = state;

  if (notifications.length >= maxItems) {
    return state;
  }

  const newNotifications = processQueue(state);

  return handleCloseOldest(newNotifications);
};

/**
 * Adds new notification to the `queue` and passes it onto `handleDisplayItem`.
 */
function handleAddNotification(state: State, action: Action): State {
  if (action.type !== ActionTypes.ADD_NOTIFICATION) {
    return state;
  }

  const { notification } = action;

  return handleDisplayItem({
    ...state,
    queue: [...state.queue, notification],
  });
}

/**
 * Marks notification as closed before it gets removed completely by `handleRemoveNotification`.
 */
function handleCloseNotification(state: State, action: Action): State {
  if (action.type !== ActionTypes.CLOSE_NOTIFICATION) {
    return state;
  }

  const notifications = state.notifications.map(item => {
    if (item.id !== action.id) {
      return item;
    }

    return {
      ...item,
      open: false,
    };
  });

  return {
    ...state,
    notifications,
  };
}

/**
 * Removes the notification from state by using the specified `id`.
 */
function handleRemoveNotification(state: State, action: Action): State {
  if (action.type !== ActionTypes.REMOVE_NOTIFICATION) {
    return state;
  }

  const { id } = action;

  const filteredNotifications = state.notifications.filter(
    item => item.id !== id,
  );

  const newNotifications = processQueue({
    ...state,
    notifications: filteredNotifications,
  });

  return handleCloseOldest(newNotifications);
}

/** Generates a random id. */
function createId(): string {
  const number: number = Math.random(); // => e.g. 0.9394456857981651
  return number.toString(36).substr(2, 9); // => e.g. 'xtis06h6'
}

/**
 * Resolves the correct notification type to be backwardscompatible with older implementations.
 * https://design-system.dm-drogeriemarkt.com/?path=/docs/molek%C3%BCle-notification--ubersicht#auspr%C3%A4gungen
 */
function resolveNotificationType({
  type = 'neutral',
}: {
  type: NotificationType;
}): Notification['kind'] {
  switch (type) {
    case 'success': {
      return 'positive';
    }
    case 'notice': {
      return 'neutral';
    }
    default: {
      return type;
    }
  }
}

/** Creates a new notification object based on the specified parameters. */
const createNotification = ({
  type,
  title,
  message,
  link,
  persist,
}: {
  type: NotificationType;
  title: Notification['title'];
  message: Notification['message'];
  link: Notification['link'];
  persist: Notification['persist'];
}): Notification => {
  const id = createId();
  return {
    id,
    kind: resolveNotificationType({ type }),
    title,
    message,
    link,
    persist,
    open: true,
    requestClose: false,
  };
};

export const notificationReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ActionTypes.ADD_NOTIFICATION: {
      return handleAddNotification(state, action);
    }
    case ActionTypes.CLOSE_NOTIFICATION: {
      return handleCloseNotification(state, action);
    }
    case ActionTypes.REMOVE_NOTIFICATION: {
      return handleRemoveNotification(state, action);
    }
    default: {
      throw new Error('Action type does not match.');
    }
  }
};

const NotificationProvider: React.FC<ProviderProps> = ({
  children,
  items = [],
  maxItems = 5,
  autoHideDuration = 5000,
}) => {
  const [state, dispatch] = useReducer(notificationReducer, {
    maxItems,
    notifications: items,
    queue: [],
  });

  /** Add and enqueue a new notification. */
  const enqueue = React.useCallback(
    ({
      type = 'neutral',
      title,
      message,
      link,
      persist = false,
    }: EnqueueProps): Notification['id'] => {
      const newNotification = createNotification({
        type,
        title,
        message,
        link,
        persist,
      });

      dispatch({
        type: ActionTypes.ADD_NOTIFICATION,
        notification: newNotification,
      });

      return newNotification.id;
    },
    [],
  );

  /** Mark a notification as closed by passing it's `id`. */
  const close = React.useCallback((id: string): void => {
    dispatch({
      type: ActionTypes.CLOSE_NOTIFICATION,
      id,
    });
  }, []);

  /**
   * Remove a notification by it's `id`.
   * Delay the removal for `200ms`, just to make it a bit more pleasant.
   */
  const remove = React.useCallback((id: string): void => {
    setTimeout(() => {
      dispatch({
        type: ActionTypes.REMOVE_NOTIFICATION,
        id,
      });
    }, 200);
  }, []);

  const notificationContextValue: MutableRefObject<Context> = React.useRef({
    enqueue,
    close,
  });

  const notificationItems = state.notifications.map(
    ({ id, ...otherProps }): React.ReactNode => (
      <NotificationItem
        {...{
          ...otherProps,
          id,
          key: id,
          onClose: close,
          onExited: remove,
          timeout: 200,
          autoHideDuration,
        }}
      />
    ),
  );

  return (
    <NotificationContext.Provider value={notificationContextValue.current}>
      {children}
      <NotificationContainer dmId="interactive-notifications-container">
        {notificationItems}
      </NotificationContainer>
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
